/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */
import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  memo,
  lazy,
  Suspense,
} from "react";
import {
  UseFormRegister,
  FieldErrors,
  UseFormSetValue,
  UseFormClearErrors,
  Control,
  useWatch,
} from "react-hook-form";
import SelectInput from "../../../components/Select";
import FormErrorMessage from "../../../components/common/FormErrorMessage";
import {
  GULF_COUNTRIES,
  parseNominatimResult,
  getCountryAddressConfig,
} from "../../OrderManagement/_components/GulfMapPicker";
import { useDistrictFetchData } from "../../../apis/locationApis";
import NewinputField from "../../../components/NewInput";
import NewSelectInput from "../../../components/NewSelectInput";
import { useLanguage } from "../../../context/LanguageContext";

const GulfMapPicker = lazy(
  () => import("../../OrderManagement/_components/GulfMapPicker"),
);

const API_BASE = "https://track-map.gwarm.net/api/areas";

interface PickupAddressPickerProps {
  register: UseFormRegister<any>;
  setValue: UseFormSetValue<any>;
  errors: FieldErrors<any>;
  clearErrors: UseFormClearErrors<any>;
  control: Control<any>;
  countries: any[];
  cities: any[];
  selectedCountry: any;
  selectedCity: any;
  selectedDistrict: any;
  onCountryChange: (country: any) => void;
  onCityChange: (city: any) => void;
  onDistrictChange: (district: any) => void;
  mode?: string;
  fieldPrefix?: string;
}

const Spinner = memo(() => (
  <svg
    className="animate-spin h-4 w-4 text-blue-500"
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
  >
    <circle
      className="opacity-25"
      cx="12"
      cy="12"
      r="10"
      stroke="currentColor"
      strokeWidth="4"
    />
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
  </svg>
));

const MapSkeleton = () => (
  <div
    style={{
      height: 260,
      borderRadius: 12,
      border: "1px solid #e5e7eb",
      background: "#f3f4f6",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    }}
  >
    <span style={{ color: "#9ca3af", fontSize: 13 }}>Loading map…</span>
  </div>
);

const PickupAddressPicker: React.FC<PickupAddressPickerProps> = ({
  register,
  errors,
  setValue,
  control,
  clearErrors,
  countries = [],
  cities = [],
  selectedCountry,
  selectedCity,
  selectedDistrict,
  onCountryChange,
  onCityChange,
  onDistrictChange,
  mode,
  fieldPrefix,
}) => {
  const { t } = useLanguage();
  const [showMap, setShowMap] = useState(false);
  const [pinPosition, setPinPosition] = useState<{
    lat: number;
    lng: number;
  } | null>(null);
  const [snappedPosition, setSnappedPosition] = useState<{
    lat: number;
    lng: number;
  } | null>(null);
  const [shortAddressLoading, setShortAddressLoading] = useState(false);
  const [shortAddressError, setShortAddressError] = useState<string>("");
  const [shortAddressWarning, setShortAddressWarning] = useState<string>("");
  const [searchRef, setSearchRef] = useState<string>("");

  const isView = mode === "view";

  const getFieldName = useCallback(
    (name: string) => {
      if (!fieldPrefix) return name;
      return `${fieldPrefix}${name.charAt(0).toUpperCase()}${name.slice(1)}`;
    },
    [fieldPrefix],
  );

  const watchLatitude = useWatch({ control, name: getFieldName("latitude") });
  const watchLongitude = useWatch({ control, name: getFieldName("longitude") });

  useEffect(() => {
    if (watchLatitude && watchLongitude) {
      const lat = parseFloat(watchLatitude);
      const lng = parseFloat(watchLongitude);
      if (!isNaN(lat) && !isNaN(lng)) {
        setPinPosition({ lat, lng });
      }
    } else {
      setPinPosition(null);
    }
  }, [watchLatitude, watchLongitude]);

  const countryName = selectedCountry?.name || "";
  const cityId = selectedCity?.id;

  const countryCode = useMemo(
    () =>
      GULF_COUNTRIES.find(
        (c) => c.name.toLowerCase() === countryName?.toLowerCase(),
      )?.code || "OTHER",
    [countryName],
  );

  const addressConfig = useMemo(
    () => getCountryAddressConfig(countryCode),
    [countryCode],
  );

  const {
    data: districtData = [],
    isPending: isDistrictFetchPending,
    refetch: fetchDistricts,
  } = useDistrictFetchData(cityId, searchRef, "map");

  const handleDistrictSearch = useCallback(
    (query: string) => {
      if (query.trim().length >= 3) {
        setSearchRef(query);
        fetchDistricts();
      }
    },
    [fetchDistricts],
  );

  const applyAddressFromApi = useCallback(
    (addr: any) => {
      const fullAddr =
        `${addr.buildingNumber || ""} ${addr.street || ""} ${addr.district || ""} ${addr.city || ""}`.trim();
      if (fullAddr)
        setValue(getFieldName("fullAddress"), fullAddr, {
          shouldValidate: true,
        });

      if (addr.city) {
        const matchedCity = cities?.find(
          (c: any) => c.name.toLowerCase() === addr.city?.toLowerCase(),
        );
        if (matchedCity) {
          onCityChange(matchedCity);
          setValue(getFieldName("cityId"), matchedCity.id, {
            shouldValidate: true,
          });
        } else {
          onCityChange({ name: addr.city, id: addr.city });
          setValue(getFieldName("cityId"), addr.city, { shouldValidate: true });
        }
      }

      if (addr.district) {
        const matchedDist = districtData?.find(
          (d: any) => d.name.toLowerCase() === addr.district?.toLowerCase(),
        );
        if (matchedDist) {
          onDistrictChange(matchedDist);
          setValue(getFieldName("districtId"), matchedDist.id, {
            shouldValidate: true,
          });
        } else {
          onDistrictChange({ name: addr.district, id: addr.district });
          setValue(getFieldName("districtId"), addr.district, {
            shouldValidate: true,
          });
        }
      }

      if (addr.postalCode)
        setValue(getFieldName("pinCode"), addr.postalCode, {
          shouldValidate: true,
        });
    },
    [
      cities,
      districtData,
      setValue,
      onCityChange,
      onDistrictChange,
      getFieldName,
    ],
  );

  const watchShortAddress = useWatch({
    control,
    name: getFieldName("shortAddress"),
  });
  const watchFullAddress = useWatch({
    control,
    name: getFieldName("fullAddress"),
  });
  const shortAddressOk =
    !shortAddressLoading &&
    !shortAddressError &&
    watchShortAddress?.length === 8 &&
    watchFullAddress;

  const handleMapPick = useCallback(
    async (latLng: { lat: number; lng: number }, mapData: any) => {
      if (isView) return;
      setPinPosition(latLng);
      setSnappedPosition(null);
      setValue(getFieldName("latitude"), latLng.lat.toString());
      setValue(getFieldName("longitude"), latLng.lng.toString());

      let activeCountryCode = countryCode;
      const detectedCode = mapData?.address?.country_code?.toUpperCase();

      if (
        detectedCode &&
        detectedCode !== countryCode &&
        GULF_COUNTRIES.some((c) => c.code === detectedCode)
      ) {
        activeCountryCode = detectedCode;
        const gulfName =
          GULF_COUNTRIES.find((c) => c.code === detectedCode)?.name || "";
        const matchedCountry = countries.find(
          (c: any) => c.name.toLowerCase() === gulfName.toLowerCase(),
        );
        if (matchedCountry) {
          onCountryChange(matchedCountry);
          setValue(getFieldName("countryId"), matchedCountry.id);
        }
      }

      if (activeCountryCode === "SA") {
        setShortAddressLoading(true);
        setShortAddressError("");
        setShortAddressWarning("");
        setValue(getFieldName("shortAddress"), "");

        // 1. Check if google map (Nominatim) data contains short address
        let foundShortAddress: string | null = null;
        if (mapData) {
          const textToSearch = JSON.stringify(mapData);
          const match = textToSearch.match(/\b([a-zA-Z]{4}\d{4})\b/);
          if (match) {
            foundShortAddress = match[1].toUpperCase();
          }
        }

        if (foundShortAddress) {
          setValue(getFieldName("shortAddress"), foundShortAddress);

          try {
            const checkRes = await fetch(
              `${API_BASE}/adrress-get-by-short-address?shortAddress=${foundShortAddress}`,
            );
            const checkData = await checkRes.json();
            if (checkData?.success && checkData?.data) {
              const shortAddr = checkData.data;
              applyAddressFromApi(shortAddr);

              if (shortAddr.latitude && shortAddr.longitude) {
                const pos = {
                  lat: parseFloat(shortAddr.latitude),
                  lng: parseFloat(shortAddr.longitude),
                };
                setPinPosition(pos);
                setSnappedPosition(pos);
                setValue(
                  getFieldName("latitude"),
                  shortAddr.latitude.toString(),
                );
                setValue(
                  getFieldName("longitude"),
                  shortAddr.longitude.toString(),
                );
              }
              setShortAddressWarning("");
              setShortAddressError("");
            } else {
              setShortAddressError(
                "Short Address verified lookup failed. Please type it manually.",
              );
            }
          } catch (err) {
            console.error("Short address verification lookup failed:", err);
            setShortAddressError(
              "Could not verify short address. Please try again.",
            );
          } finally {
            setShortAddressLoading(false);
          }
          return;
        }

        // 2. Check coordinates API
        try {
          const res = await fetch(
            `${API_BASE}/find-by-coordinates?lat=${latLng.lat}&lon=${latLng.lng}`,
          );
          const apiData = await res.json();

          if (apiData?.success && apiData?.data) {
            const addr = apiData.data;
            const source: string = addr.source ?? "database";
            applyAddressFromApi(addr);

            if (source === "redis_geo" || source === "database_zone") {
              if (addr.latitude && addr.longitude) {
                const snapped = {
                  lat: parseFloat(addr.latitude),
                  lng: parseFloat(addr.longitude),
                };
                setSnappedPosition(snapped);
                setPinPosition(snapped);
                setValue(getFieldName("latitude"), addr.latitude.toString());
                setValue(getFieldName("longitude"), addr.longitude.toString());
              }
            }

            const resolvedShortCode = addr.name;
            if (resolvedShortCode) {
              setValue(getFieldName("shortAddress"), resolvedShortCode);

              try {
                const checkRes = await fetch(
                  `${API_BASE}/adrress-get-by-short-address?shortAddress=${resolvedShortCode}`,
                );
                const checkData = await checkRes.json();
                if (checkData?.success && checkData?.data) {
                  const shortAddr = checkData.data;
                  applyAddressFromApi(shortAddr);

                  if (shortAddr.latitude && shortAddr.longitude) {
                    const pos = {
                      lat: parseFloat(shortAddr.latitude),
                      lng: parseFloat(shortAddr.longitude),
                    };
                    setPinPosition(pos);
                    setSnappedPosition(pos);
                    setValue(
                      getFieldName("latitude"),
                      shortAddr.latitude.toString(),
                    );
                    setValue(
                      getFieldName("longitude"),
                      shortAddr.longitude.toString(),
                    );
                  }

                  if (source === "database_zone") {
                    setShortAddressWarning(
                      "📍 Approximate match. Pin again for precise address.",
                    );
                  } else {
                    setShortAddressWarning("");
                  }
                  setShortAddressError("");
                } else {
                  setShortAddressError(
                    "Short Address verified lookup failed. Please type it manually.",
                  );
                }
              } catch (err) {
                console.error("Short address verification lookup failed:", err);
                setShortAddressError(
                  "Could not verify short address. Please try again.",
                );
              }
            } else {
              setValue(getFieldName("shortAddress"), "");
              setShortAddressError(
                "Short Address was not found from the map. Please fill it manually.",
              );
            }
            setShortAddressLoading(false);
            return;
          }
        } catch (err) {
          console.error("Map pick API error:", err);
        }

        // 3. Fallback: geocoding but no short address found
        setShortAddressLoading(false);
        setValue(getFieldName("shortAddress"), "");
        setShortAddressWarning("");
        setShortAddressError(
          "Short Address was not found from the map. Please fill it manually.",
        );
      }

      const parsed: any = parseNominatimResult(mapData, activeCountryCode);
      applyAddressFromApi(parsed);
    },
    [
      countryCode,
      countries,
      onCountryChange,
      setValue,
      applyAddressFromApi,
      countryName,
      watchShortAddress,
      isView,
    ],
  );

  const handleShortAddressChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      if (isView) return;
      const val = e.target.value.toUpperCase();
      setValue(getFieldName("shortAddress"), val);
      setShortAddressWarning("");
      setShortAddressError("");

      if (val.length === 8) {
        setShortAddressLoading(true);
        try {
          const res = await fetch(
            `${API_BASE}/adrress-get-by-short-address?shortAddress=${val}`,
          );
          const apiData = await res.json();
          if (apiData?.success && apiData?.data) {
            const addr = apiData.data;
            applyAddressFromApi(addr);
            if (addr.latitude && addr.longitude) {
              const pos = {
                lat: parseFloat(addr.latitude),
                lng: parseFloat(addr.longitude),
              };
              setPinPosition(pos);
              setSnappedPosition(pos);
              setValue(getFieldName("latitude"), addr.latitude.toString());
              setValue(getFieldName("longitude"), addr.longitude.toString());
            }
          } else {
            setShortAddressError("Short code not found.");
          }
        } catch (err) {
          setShortAddressError("Verification failed.");
        } finally {
          setShortAddressLoading(false);
        }
      }
    },
    [setValue, applyAddressFromApi, isView],
  );

  const isSaudiArabia = countryCode === "SA";

  return (
    <div className="  space-y-4">
      {/* Map Toggle */}
      <div className="flex justify-between items-center bg-white/50 p-3 rounded-xl border border-[#D8D8D8]">
        <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
          {t("Pin Location")}
          {!!pinPosition && (
            <span className="px-2 py-0.5 rounded-full bg-green-100 text-green-700 text-xs font-bold">
              📍 {t("Pinned")}
            </span>
          )}
        </label>
        <button
          type="button"
          onClick={() => setShowMap(!showMap)}
          className="px-4 py-1.5 rounded-full bg-[#4F6EF7] text-white text-xs font-semibold hover:bg-[#3d59c9] transition-colors"
        >
          {showMap ? t("Hide Map") : t("Open Map")}
        </button>
      </div>

      {showMap && (
        <Suspense fallback={<MapSkeleton />}>
          <GulfMapPicker
            country={countryCode}
            onPick={handleMapPick}
            pinPosition={pinPosition}
            snappedPosition={snappedPosition}
          />
        </Suspense>
      )}

      <div className="grid grid-cols-2 gap-4">
        <SelectInput
           
          name={getFieldName("countryId")}
          label="Country"
          placeholder="Select Country"
          selectOptions={countries.map((c) => ({
            id: c.id,
            value: c.id,
            text: c.name,
          }))}
          register={register}
          clearErrors={clearErrors}
          validation={{ required: "Country is required" }}
          error={errors[getFieldName("countryId")]}
          value={selectedCountry?.id}
          setValue={(val: string) => {
            const matched = countries.find((c) => c.id === val);
            if (matched) onCountryChange(matched);
            setValue(getFieldName("countryId"), val);
            setValue(getFieldName("cityId"), "");
            setValue(getFieldName("districtId"), "");
            onCityChange(null);
            onDistrictChange(null);
          }}
          disabled={isView}
        />

        {/* 2. Short Address */}
        {addressConfig.hasShortAddress && (
          <div className="  ">
            <label className="text-sm text-[#1E293B]  ">
              {t("Short Address")}{countryCode === "SA" && <span className="text-red-500 ml-0.5">*</span>}
            </label>
            <div className="space-y-1 w-full">
              <div className="relative">
                <input
                  {...(() => {
                    const { onChange: formOnChange, ...rest } = register(
                      getFieldName("shortAddress"),
                      {
                        required: countryCode === "SA" ? "Short address is required" : false,
                        validate: (val) => {
                          if (
                            countryCode === "SA" &&
                            val &&
                            val.length !== 8
                          ) {
                            return "Short address must be 8 characters";
                          }
                          return true;
                        },
                      },
                    );
                    return {
                      ...rest,
                      onChange: (e: any) => {
                        formOnChange(e);
                        handleShortAddressChange(e);
                      },
                    };
                  })()}
                  placeholder="e.g. RHOO2375"
                  disabled={isView}
                  className={`w-full px-4 py-2 text-sm rounded-xl outline-none bg-white border pr-10 ${
                    shortAddressError
                      ? "border-red-400 bg-red-50"
                      : shortAddressOk
                        ? "border-green-400 bg-green-50"
                        : "border-[#D8D8D8]"
                  } focus:border-[#4F6EF7]`}
                />
                <div className="absolute inset-y-0 right-3 flex items-center gap-1">
                  {shortAddressLoading && <Spinner />}
                  {!shortAddressLoading && shortAddressError && (
                    <span className="text-red-500 text-base">✕</span>
                  )}
                  {shortAddressOk && (
                    <span className="text-green-500 text-base font-bold">
                      ✓
                    </span>
                  )}
                </div>
              </div>
              {shortAddressError && (
                <span className="text-xs text-red-500">
                  {shortAddressError}
                </span>
              )}
              {shortAddressWarning && (
                <span className="text-xs text-amber-600 font-medium">
                  {shortAddressWarning}
                </span>
              )}
              {errors[getFieldName("shortAddress")] && !shortAddressError && (
                <FormErrorMessage
                  error={errors[getFieldName("shortAddress")]}
                />
              )}
            </div>
          </div>
        )}

        <SelectInput
          
          name={getFieldName("cityId")}
          label={addressConfig.cityLabel}
          placeholder={addressConfig.cityPlaceholder}
          selectOptions={cities.map((c) => ({
            id: c.id,
            value: c.id,
            text: c.name,
          }))}
          register={register}
          clearErrors={clearErrors}
          validation={{ required: `${addressConfig.cityLabel} is required` }}
          error={errors[getFieldName("cityId")]}
          value={selectedCity?.id}
          disabled={isView}
          allowCustom={true}
          setValue={(val: string) => {
            const matched = cities.find((c) => c.id === val);
            if (matched) {
              onCityChange(matched);
            } else {
              onCityChange({ name: val, id: val });
            }
            setValue(getFieldName("cityId"), val);
            setValue(getFieldName("districtId"), "");
            onDistrictChange(null);
          }}
        />

        <SelectInput
          
          name={getFieldName("districtId")}
          label={addressConfig.districtLabel}
          placeholder={addressConfig.districtPlaceholder}
          selectOptions={districtData.map((d: any) => ({
            id: d.id,
            value: d.id,
            text: d.name,
          }))}
          register={register}
          clearErrors={clearErrors}
          validation={{
            required: `${addressConfig.districtLabel} is required`,
          }}
          error={errors[getFieldName("districtId")]}
          value={selectedDistrict?.id}
          disabled={isView}
          onSearch={handleDistrictSearch}
          isPending={isDistrictFetchPending}
          allowCustom={true}
          setValue={(val: string) => {
            const matched = districtData.find((d: any) => d.id === val);
            if (matched) {
              onDistrictChange(matched);
            } else {
              onDistrictChange({ name: val, id: val });
            }
            setValue(getFieldName("districtId"), val);
          }}
        />

        <NewinputField
           
          name={getFieldName("pinCode")}
          type="text"
          title={addressConfig.zipLabel}
          placeholder={addressConfig.zipPlaceholder}
          register={register}
          error={errors[getFieldName("pinCode")]}
          validation={
            addressConfig.zipRequired
              ? {
                  required: `${addressConfig.zipLabel} is required`,
                  pattern: {
                    value: /^\d+$/,
                    message: `${addressConfig.zipLabel} must contain only digits`,
                  },
                }
              : undefined
          }
          disabled={isView}
        />

        <NewinputField
         
          name={getFieldName("fullAddress")}
          type="text"
          title="Full Address"
          placeholder="Street, Building..."
          register={register}
          error={errors[getFieldName("fullAddress")]}
          validation={{ required: "Address is required" }}
          disabled={isView}
        />

        {/* Latitude & Longitude (Hidden) */}
        <input type="hidden" {...register(getFieldName("latitude"))} />
        <input type="hidden" {...register(getFieldName("longitude"))} />
      </div>
    </div>
  );
};

export default memo(PickupAddressPicker);
